import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Text,
  Card,
  CardBody,
  VStack,
  HStack,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useToast,
  Badge,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useBreakpointValue,
  Checkbox,
  CheckboxGroup,
  Image,
  Divider,
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import { FiEdit, FiTrash2, FiPlus, FiRefreshCw, FiEye, FiImage, FiHome, FiFolder } from 'react-icons/fi';
import { hotelApi, hotelImageApi, hotelRoomTypeApi } from '../config/api';
import { useAuth } from '../context/AuthContext';
import IeemPublishTab from '../components/IeemPublishTab';
import IeemFlyerTab from '../components/IeemFlyerTab';
import IeemIframeTab from '../components/IeemIframeTab';
import BundlingProductTab from '../components/BundlingProductTab';
import PageHeader from '../components/bundling/PageHeader';
import HinHotelMaster from './HinHotelMaster';

const Hospitality = () => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [viewingHotel, setViewingHotel] = useState(null);
  const [deleteHotel, setDeleteHotel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Hotel Image states
  const [managingImagesHotel, setManagingImagesHotel] = useState(null);
  const [hotelImages, setHotelImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [deleteImage, setDeleteImage] = useState(null);
  
  // Hotel Room Types states
  const [roomTypes, setRoomTypes] = useState([]);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState(null);
  const [deleteRoomType, setDeleteRoomType] = useState(null);
  const { isOpen: isRoomTypeFormOpen, onOpen: onRoomTypeFormOpen, onClose: onRoomTypeFormClose } = useDisclosure();
  const { isOpen: isDeleteRoomTypeOpen, onOpen: onDeleteRoomTypeOpen, onClose: onDeleteRoomTypeClose } = useDisclosure();
  const [roomTypeFormData, setRoomTypeFormData] = useState({
    ROOMTYPE_CODE: '',
    ROOMTYPE_NAME: '',
    ROOMTYPE_BASEOCCUPANCY: '',
    ROOMTYPE_MAXOCCUPANCY: '',
    ROOMTYPE_DESCRIPTION: '',
    ROOMTYPE_DISCOUNTPRICE: '',
    ROOMTYPE_FIXEDPRICE: '',
  });
  
  
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedRole, user } = useAuth();
  
  // Determine active tab based on route - hanya untuk IEEM sub menu
  const isIeemRoute = location.pathname.includes('/hospitality/ieem') || location.pathname === '/admin/hospitality/ieem';
  const isBundlingProductRoute = location.pathname.includes('/hospitality/bundling-product') || location.pathname === '/admin/hospitality/bundling-product';
  
  // Tab hanya untuk IEEM: How to Publish dan List Product
  const [activeIeemTab, setActiveIeemTab] = useState(0);
  
  // Update active tab when route changes
  useEffect(() => {
    if (location.pathname === '/admin/hospitality/ieem' || location.pathname === '/admin/hospitality') {
      // Default redirect to IEEM if no sub route specified
      navigate('/admin/hospitality/ieem', { replace: true });
    }
  }, [location.pathname, navigate]);
  
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isImagesOpen, onOpen: onImagesOpen, onClose: onImagesClose } = useDisclosure();
  const { isOpen: isDeleteImageOpen, onOpen: onDeleteImageOpen, onClose: onDeleteImageClose } = useDisclosure();
  const cancelRef = React.useRef();
  const cancelImageRef = React.useRef();
  
  // Mapping IEMMPUBLISH_MEMBER berdasarkan route
  const getMemberCode = useCallback(() => {
    const path = location.pathname;
    if (path.includes('/airports')) return 'api';
    if (path.includes('/aviation-services')) return 'ias';
    if (path.includes('/retail')) return 'srn';
    if (path.includes('/hospitality')) return 'hin';
    if (path.includes('/destination-management')) return 'twc';
    if (path.includes('/tourism-development')) return 'itdc';
    return 'hin'; // default untuk hospitality
  }, [location.pathname]);
  

  // Check user role untuk menentukan tab yang muncul
  const userRole = user?.role || '';
  let parsedUserRoles = [];
  if (userRole) {
    if (Array.isArray(userRole)) {
      parsedUserRoles = userRole;
    } else if (typeof userRole === 'string') {
      if (userRole.includes(';')) {
        parsedUserRoles = userRole.split(';').map(r => r.trim()).filter(r => r);
      } else if (userRole.includes(',')) {
        parsedUserRoles = userRole.split(',').map(r => r.trim()).filter(r => r);
      } else if (userRole.trim()) {
        parsedUserRoles = [userRole.trim()];
      }
    }
  }
  
  const hasRoleIeem = selectedRole === 'ieem' || (!selectedRole && parsedUserRoles.includes('ieem'));
  const hasRoleBp = selectedRole === 'bp' || (!selectedRole && parsedUserRoles.includes('bp'));

  const [formData, setFormData] = useState({
    HOTEL_NAME: '',
    HOTEL_STAR: '',
    HOTEL_LOCATION: '',
    HOTEL_DESCRIPTION: '',
    HOTEL_ROOMOPTION: [],
    HOTEL_DISCOUNT: '',
    HOTEL_FIXEDPRICE: '',
    HOTEL_PUBLISHRATE: '0',
    HOTEL_CONTACTPERSON: '',
    HOTEL_PHONE: '',
    HOTEL_EMAIL: '',
    HOTEL_BANKACCOUNT: '',
    HOTEL_BANKNAME: '',
    HOTEL_TERMCONDITION: '',
    HOTEL_MSG: '',
    HOTEL_BOOKINGURL: '',
    HOTEL_STATUS: '1',
  });

  // Room Option options
  const roomOptions = [
    'Standart Room',
    'Superior Room',
    'Deluxe Room',
    'Executive Room',
    'Suite Room',
    'Junior Suite',
    'Deluxe Difable',
    'Deluxe King / Twin Room',
    'Deluxe Suite',
    'Family Room',
    'Connecting Room',
    'Presidential Suite',
    'Studio Room',
    'Twin Room',
    'Single Room',
    'Triple Room',
    'Villa / Cottage / Bungalow',
  ];

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    try {
      // Gunakan endpoint /list dengan filter memberId = 'hin' untuk Hospitality
      // includeInactive=true untuk menampilkan semua data termasuk non-active (untuk admin)
      const memberId = getMemberCode(); // Returns 'hin' for hospitality
      const response = await hotelApi.get('/list', {
        params: { 
          memberId,
          includeInactive: true // Show all statuses including non-active for admin
        }
      });
      console.log('Hotel API Response:', response.data);
      
      if (response.data.status === 200) {
        const hotelsData = response.data.data || [];
        console.log('Hotels data:', hotelsData);
        setHotels(hotelsData);
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Gagal mengambil data hotel',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching hotels:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast, getMemberCode]);

  useEffect(() => {
    // Fetch hotels jika di tab List Product (hanya untuk IEEM route)
    if (hasRoleIeem && isIeemRoute && activeIeemTab === 1) {
      fetchHotels();
    }
  }, [fetchHotels, hasRoleIeem, isIeemRoute, activeIeemTab]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle room option change
  const handleRoomOptionChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      HOTEL_ROOMOPTION: selectedOptions,
    }));
  };

  const handleCreate = () => {
    setEditingHotel(null);
    setFormData({
      HOTEL_CODE: '',
      HOTEL_NAME: '',
      HOTEL_STAR: '',
      HOTEL_LOCATION: '',
      HOTEL_DESCRIPTION: '',
      HOTEL_ROOMOPTION: [],
      HOTEL_DISCOUNT: '',
      HOTEL_FIXEDPRICE: '',
      HOTEL_PUBLISHRATE: '0',
      HOTEL_CONTACTPERSON: '',
      HOTEL_PHONE: '',
      HOTEL_EMAIL: '',
      HOTEL_BANKACCOUNT: '',
      HOTEL_BANKNAME: '',
      HOTEL_TERMCONDITION: '',
      HOTEL_MSG: '',
      HOTEL_BOOKINGURL: '',
      HOTEL_STATUS: '1',
    });
    onFormOpen();
  };

  const handleEdit = async (hotel) => {
    setEditingHotel(hotel);
    // Fetch room types for this hotel
    if (hotel.HOTEL_ID) {
      await fetchRoomTypes(hotel.HOTEL_ID);
    }
    // Parse HOTEL_ROOMOPTION dari string ke array
    let roomOptionArray = [];
    if (hotel.HOTEL_ROOMOPTION) {
      if (Array.isArray(hotel.HOTEL_ROOMOPTION)) {
        roomOptionArray = hotel.HOTEL_ROOMOPTION;
      } else if (typeof hotel.HOTEL_ROOMOPTION === 'string') {
        // Support comma atau semicolon separator
        if (hotel.HOTEL_ROOMOPTION.includes(';')) {
          roomOptionArray = hotel.HOTEL_ROOMOPTION.split(';').map(r => r.trim()).filter(r => r);
        } else if (hotel.HOTEL_ROOMOPTION.includes(',')) {
          roomOptionArray = hotel.HOTEL_ROOMOPTION.split(',').map(r => r.trim()).filter(r => r);
        } else if (hotel.HOTEL_ROOMOPTION.trim()) {
          roomOptionArray = [hotel.HOTEL_ROOMOPTION.trim()];
        }
      }
    }
    
    setFormData({
      HOTEL_CODE: hotel.HOTEL_CODE || '',
      HOTEL_NAME: hotel.HOTEL_NAME || '',
      HOTEL_STAR: hotel.HOTEL_STAR?.toString() || '',
      HOTEL_LOCATION: hotel.HOTEL_LOCATION || '',
      HOTEL_DESCRIPTION: hotel.HOTEL_DESCRIPTION || '',
      HOTEL_ROOMOPTION: roomOptionArray,
      HOTEL_DISCOUNT: hotel.HOTEL_DISCOUNT || '',
      HOTEL_FIXEDPRICE: hotel.HOTEL_FIXEDPRICE || '',
      HOTEL_PUBLISHRATE: hotel.HOTEL_PUBLISHRATE?.toString() || '0',
      HOTEL_CONTACTPERSON: hotel.HOTEL_CONTACTPERSON || '',
      HOTEL_PHONE: hotel.HOTEL_PHONE || '',
      HOTEL_EMAIL: hotel.HOTEL_EMAIL || '',
      HOTEL_BANKACCOUNT: hotel.HOTEL_BANKACCOUNT || '',
      HOTEL_BANKNAME: hotel.HOTEL_BANKNAME || '',
      HOTEL_TERMCONDITION: hotel.HOTEL_TERMCONDITION || '',
      HOTEL_MSG: hotel.HOTEL_MSG || '',
      HOTEL_BOOKINGURL: hotel.HOTEL_BOOKINGURL || '',
      HOTEL_STATUS: hotel.HOTEL_STATUS?.toString() || '1',
    });
    onFormOpen();
  };

  const handleSubmit = async () => {
    // Validasi required fields sesuai controller
    if (!formData.HOTEL_NAME || !formData.HOTEL_STAR || !formData.HOTEL_LOCATION) {
      toast({
        title: 'Validasi gagal',
        description: 'Nama hotel, bintang, dan lokasi harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Prepare data dengan HOTEL_ROOMOPTION sebagai string (semicolon separated)
      const submitData = {
        ...formData,
        HOTEL_ROOMOPTION: Array.isArray(formData.HOTEL_ROOMOPTION) && formData.HOTEL_ROOMOPTION.length > 0
          ? formData.HOTEL_ROOMOPTION.join(';')
          : (formData.HOTEL_ROOMOPTION || null),
      };

      // Add MEMBER_ID to submit data
      const memberId = getMemberCode(); // Returns 'hin' for hospitality
      const finalSubmitData = {
        ...submitData,
        MEMBER_ID: memberId
      };

      if (editingHotel) {
        // Update - menggunakan POST /update/:id
        const response = await hotelApi.post(`/update/${editingHotel.HOTEL_ID}`, finalSubmitData);
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Hotel berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          fetchHotels();
        }
      } else {
        // Create - menggunakan POST /
        const response = await hotelApi.post('/', finalSubmitData);
        if (response.data.status === 201 || response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Hotel berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          fetchHotels();
        }
      }
    } catch (error) {
      console.error('Error saving hotel:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleView = async (hotel) => {
    setViewingHotel(hotel);
    onViewOpen();
    // Fetch room types for this hotel
    if (hotel.HOTEL_ID) {
      await fetchRoomTypes(hotel.HOTEL_ID);
    }
  };
  
  // Room Types Functions
  const fetchRoomTypes = async (hotelId) => {
    try {
      setLoadingRoomTypes(true);
      const response = await hotelRoomTypeApi.get(`/hotel/${hotelId}`);
      if (response.data.status === 200) {
        setRoomTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching room types:', error);
      setRoomTypes([]);
    } finally {
      setLoadingRoomTypes(false);
    }
  };
  
  const handleCreateRoomType = async (hotel = null) => {
    // Prioritize: passed hotel parameter > viewingHotel > editingHotel
    let targetHotel = hotel;
    
    // If no hotel parameter passed, use viewingHotel if available, otherwise editingHotel
    if (!targetHotel) {
      if (viewingHotel) {
        targetHotel = viewingHotel;
      } else if (editingHotel) {
        targetHotel = editingHotel;
      }
    }
    
    if (!targetHotel) {
      toast({
        title: 'Error',
        description: 'Pilih hotel terlebih dahulu',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Get hotel ID - handle both string and number, try multiple property names
    let hotelId = targetHotel.HOTEL_ID || targetHotel.hotel_id || targetHotel.id || targetHotel.HOTEL_ID;
    
    // If still not found, log for debugging
    if (!hotelId && hotelId !== 0) {
      console.error('Hotel object:', targetHotel);
      console.error('Available keys:', Object.keys(targetHotel));
      console.error('viewingHotel:', viewingHotel);
      console.error('editingHotel:', editingHotel);
      
      toast({
        title: 'Error',
        description: 'Hotel ID tidak ditemukan. Silakan coba lagi atau refresh halaman.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    // Convert to number if it's a string representation of a number
    const numericHotelId = typeof hotelId === 'string' ? parseInt(hotelId, 10) : Number(hotelId);
    
    if (isNaN(numericHotelId)) {
      console.error('Invalid hotel ID:', hotelId);
      toast({
        title: 'Error',
        description: 'Hotel ID tidak valid',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Set hotel for room type management
    setEditingHotel(targetHotel);
    
    // Fetch room types first
    await fetchRoomTypes(numericHotelId);
    // Reset form for new room type
    setEditingRoomType(null);
    setRoomTypeFormData({
      ROOMTYPE_CODE: '',
      ROOMTYPE_NAME: '',
      ROOMTYPE_BASEOCCUPANCY: '',
      ROOMTYPE_MAXOCCUPANCY: '',
      ROOMTYPE_DESCRIPTION: '',
      ROOMTYPE_DISCOUNTPRICE: '',
      ROOMTYPE_FIXEDPRICE: '',
    });
    onRoomTypeFormOpen();
  };
  
  const handleEditRoomType = (roomType) => {
    setEditingRoomType(roomType);
    setRoomTypeFormData({
      ROOMTYPE_CODE: roomType.ROOMTYPE_CODE || '',
      ROOMTYPE_NAME: roomType.ROOMTYPE_NAME || '',
      ROOMTYPE_BASEOCCUPANCY: roomType.ROOMTYPE_BASEOCCUPANCY?.toString() || '',
      ROOMTYPE_MAXOCCUPANCY: roomType.ROOMTYPE_MAXOCCUPANCY?.toString() || '',
      ROOMTYPE_DESCRIPTION: roomType.ROOMTYPE_DESCRIPTION || '',
      ROOMTYPE_DISCOUNTPRICE: roomType.ROOMTYPE_DISCOUNTPRICE || '',
      ROOMTYPE_FIXEDPRICE: roomType.ROOMTYPE_FIXEDPRICE || '',
    });
    onRoomTypeFormOpen();
  };
  
  const handleSubmitRoomType = async () => {
    if (!roomTypeFormData.ROOMTYPE_NAME || roomTypeFormData.ROOMTYPE_NAME.trim() === '') {
      toast({
        title: 'Validasi gagal',
        description: 'Nama room type harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const hotelId = editingHotel?.HOTEL_ID || viewingHotel?.HOTEL_ID;
    if (!hotelId) {
      toast({
        title: 'Error',
        description: 'Hotel ID tidak ditemukan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      if (editingRoomType) {
        // Update
        const response = await hotelRoomTypeApi.post(`/update/${editingRoomType.ROOMTYPE_ID}`, {
          ...roomTypeFormData,
          ROOMTYPE_BASEOCCUPANCY: roomTypeFormData.ROOMTYPE_BASEOCCUPANCY ? parseInt(roomTypeFormData.ROOMTYPE_BASEOCCUPANCY) : null,
          ROOMTYPE_MAXOCCUPANCY: roomTypeFormData.ROOMTYPE_MAXOCCUPANCY ? parseInt(roomTypeFormData.ROOMTYPE_MAXOCCUPANCY) : null,
        });
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Room type berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onRoomTypeFormClose();
          await fetchRoomTypes(hotelId);
        }
      } else {
        // Create
        const response = await hotelRoomTypeApi.post('/', {
          HOTEL_ID: hotelId,
          ...roomTypeFormData,
          ROOMTYPE_BASEOCCUPANCY: roomTypeFormData.ROOMTYPE_BASEOCCUPANCY ? parseInt(roomTypeFormData.ROOMTYPE_BASEOCCUPANCY) : null,
          ROOMTYPE_MAXOCCUPANCY: roomTypeFormData.ROOMTYPE_MAXOCCUPANCY ? parseInt(roomTypeFormData.ROOMTYPE_MAXOCCUPANCY) : null,
        });
        if (response.data.status === 201) {
          toast({
            title: 'Berhasil',
            description: 'Room type berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onRoomTypeFormClose();
          await fetchRoomTypes(hotelId);
        }
      }
    } catch (error) {
      console.error('Error saving room type:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  const handleDeleteRoomTypeClick = (roomType) => {
    setDeleteRoomType(roomType);
    onDeleteRoomTypeOpen();
  };
  
  const handleDeleteRoomTypeConfirm = async () => {
    if (!deleteRoomType) return;
    
    try {
      const response = await hotelRoomTypeApi.post(`/delete/${deleteRoomType.ROOMTYPE_ID}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Room type berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteRoomTypeClose();
        setDeleteRoomType(null);
        const hotelId = editingHotel?.HOTEL_ID || viewingHotel?.HOTEL_ID;
        if (hotelId) {
          await fetchRoomTypes(hotelId);
        }
      }
    } catch (error) {
      console.error('Error deleting room type:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (hotel) => {
    setDeleteHotel(hotel);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteHotel) return;

    try {
      // Delete - menggunakan POST /delete/:id (soft delete)
      const response = await hotelApi.post(`/delete/${deleteHotel.HOTEL_ID}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: response.data.message || 'Hotel berhasil dihapus (status diubah menjadi not active)',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        setDeleteHotel(null);
        fetchHotels();
      }
    } catch (error) {
      console.error('Error deleting hotel:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Hotel Image handlers
  const handleManageImages = async (hotel) => {
    setManagingImagesHotel(hotel);
    onImagesOpen();
    await fetchHotelImages(hotel.HOTEL_ID);
  };

  const fetchHotelImages = async (hotelId) => {
    setLoadingImages(true);
    try {
      const response = await hotelImageApi.get(`/hotel/${hotelId}`);
      if (response.data.status === 200) {
        setHotelImages(response.data.data || []);
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Gagal mengambil data gambar',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching hotel images:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data gambar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingImages(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !managingImagesHotel) return;

    // Validate all files
    const invalidFiles = [];
    files.forEach((file, index) => {
      if (!file.type.startsWith('image/')) {
        invalidFiles.push({ name: file.name, reason: 'File harus berupa gambar' });
      } else if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push({ name: file.name, reason: 'Ukuran file maksimal 5MB' });
      }
    });

    if (invalidFiles.length > 0) {
      toast({
        title: 'Error Validasi',
        description: `${invalidFiles.length} file tidak valid: ${invalidFiles.map(f => f.name).join(', ')}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      e.target.value = '';
      return;
    }

    // Filter valid files
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024
    );

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    setUploadingImage(true);
    setUploadProgress({});

    try {
      // Create FormData for multiple file upload
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('HOTEL_ID', managingImagesHotel.HOTEL_ID);
      formData.append('HOTEL_NAME', managingImagesHotel.HOTEL_NAME || '');
      
      // Upload files to backend
      const response = await hotelImageApi.post('/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.status === 201) {
        const uploadedCount = response.data.data?.length || 0;
        const failedCount = response.data.failedFiles?.length || 0;
        
        if (failedCount > 0) {
          toast({
            title: 'Berhasil dengan Peringatan',
            description: `Berhasil mengupload ${uploadedCount} gambar. ${failedCount} gambar gagal.`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Berhasil',
            description: `Berhasil mengupload ${uploadedCount} gambar`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
        
        // Refresh images list
        await fetchHotelImages(managingImagesHotel.HOTEL_ID);
        
        // Reset file input
        e.target.value = '';
        setUploadProgress({});
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengupload gambar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingImage(false);
      setUploadProgress({});
    }
  };

  const handleDeleteImageClick = (image) => {
    setDeleteImage(image);
    onDeleteImageOpen();
  };

  const handleDeleteImageConfirm = async () => {
    if (!deleteImage || !managingImagesHotel) return;

    try {
      const response = await hotelImageApi.post(`/delete/${deleteImage.IMAGEHOTEL_ID}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Gambar berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteImageClose();
        setDeleteImage(null);
        await fetchHotelImages(managingImagesHotel.HOTEL_ID);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus gambar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isMobileView = useBreakpointValue({ base: true, md: false });

  // Helper function to format number to Rupiah
  const formatRupiah = (value) => {
    if (!value || value === '' || isNaN(Number(value))) return value;
    return new Intl.NumberFormat('id-ID', { 
      style: 'currency', 
      currency: 'IDR', 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(value));
  };

  // DataTable columns definition
  const columns = useMemo(
    () => [
      {
        name: 'ID',
        selector: (row) => row.HOTEL_ID,
        sortable: true,
        width: '80px',
        omit: isMobileView,
      },
      {
        name: 'Hotel Code',
        selector: (row) => row.HOTEL_CODE || '-',
        sortable: true,
        width: '110px',
        omit: isMobileView,
      },
      {
        name: 'Nama Hotel',
        selector: (row) => row.HOTEL_NAME || '-',
        sortable: true,
        cell: (row) => (
          <VStack align="start" spacing={1}>
            <Text fontWeight="600" fontSize={{ base: 'sm', lg: 'md' }}>
              {row.HOTEL_NAME || '-'}
            </Text>
            {isMobileView && (
              <Text fontSize="xs" color="gray.500">
                ID: {row.HOTEL_ID} | {row.HOTEL_LOCATION || '-'}
              </Text>
            )}
          </VStack>
        ),
      },
      {
        name: 'Lokasi',
        selector: (row) => row.HOTEL_LOCATION || '-',
        sortable: true,
        omit: isMobileView,
      },
      {
        name: 'Bintang',
        selector: (row) => row.HOTEL_STAR,
        sortable: true,
        cell: (row) => (
          <Badge colorScheme="yellow" fontSize={{ base: 'xs', lg: 'sm' }}>
            {row.HOTEL_STAR} ⭐
          </Badge>
        ),
        omit: isMobileView,
      },
      {
        name: 'Status',
        selector: (row) => row.HOTEL_STATUS,
        sortable: true,
        cell: (row) => (
          <Badge
            colorScheme={row.HOTEL_STATUS === '1' || row.HOTEL_STATUS === 1 ? 'green' : 'red'}
            fontSize={{ base: 'xs', lg: 'sm' }}
          >
            {row.HOTEL_STATUS === '1' || row.HOTEL_STATUS === 1 ? 'Active' : 'Not Active'}
          </Badge>
        ),
      },
      {
        name: 'Aksi',
        cell: (row) => (
          <HStack spacing={1} justify="center">
            <IconButton
              icon={<FiEye />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="green"
              variant="ghost"
              onClick={() => handleView(row)}
              aria-label="View"
            />
            <IconButton
              icon={<FiEdit />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="blue"
              variant="ghost"
              onClick={() => handleEdit(row)}
              aria-label="Edit"
            />
            <IconButton
              icon={<FiImage />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="purple"
              variant="ghost"
              onClick={() => handleManageImages(row)}
              aria-label="Manage Images"
            />
            <IconButton
              icon={<FiHome />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="orange"
              variant="ghost"
              onClick={() => handleCreateRoomType(row)}
              aria-label="Manage Room Types"
              title="Kelola Room Types"
            />
            <IconButton
              icon={<FiTrash2 />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="red"
              variant="ghost"
              onClick={() => handleDeleteClick(row)}
              aria-label="Delete"
            />
          </HStack>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '160px',
      },
    ],
    [isMobileView]
  );

  // Custom styles for DataTable
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f7fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
      },
    },
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
  };

  // Pagination component options
  const paginationComponentOptions = {
    rowsPerPageText: 'Baris per halaman:',
    rangeSeparatorText: 'dari',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Semua',
  };

  // Filter hotels based on search term
  const filteredHotels = useMemo(() => {
    if (!searchTerm) return hotels;
    const searchLower = searchTerm.toLowerCase();
    return hotels.filter((hotel) => {
      return (
        (hotel.HOTEL_NAME && hotel.HOTEL_NAME.toLowerCase().includes(searchLower)) ||
        (hotel.HOTEL_LOCATION && hotel.HOTEL_LOCATION.toLowerCase().includes(searchLower)) ||
        (hotel.HOTEL_STAR && hotel.HOTEL_STAR.toString().includes(searchLower)) ||
        (hotel.HOTEL_ID && hotel.HOTEL_ID.toString().includes(searchLower)) ||
        (hotel.HOTEL_CODE && hotel.HOTEL_CODE.toString().toLowerCase().includes(searchLower))
      );
    });
  }, [hotels, searchTerm]);

  const eyebrow = isIeemRoute ? 'IEEM Workspace' : isBundlingProductRoute ? 'Product Workspace' : 'Domain Workspace';

  return (
    <Box>
      <VStack align="stretch" spacing={4} mb={6}>
        <PageHeader
          eyebrow={eyebrow}
          title="Injourney Hospitality"
          description="Kelola channel IEEM, bundling product, dan integrasi data hotel Hospitality."
          secondaryActions={(
            <Button
              as={Link}
              to="/admin/hospitality/integrationData"
              leftIcon={<FiFolder />}
              size="sm"
              variant="outline"
              colorScheme="blue"
            >
              Integration Data (FTP)
            </Button>
          )}
        />
      </VStack>

      <Card bg="white" borderRadius="16px" boxShadow="sm">
        <CardBody p={{ base: 2, lg: 4 }}>
          {/* Hanya tampilkan tab jika di route IEEM */}
          {isIeemRoute && hasRoleIeem ? (
            <Tabs index={activeIeemTab} onChange={setActiveIeemTab} isLazy lazyBehavior="keepMounted">
              <TabList>
                <Tab>How To Publish</Tab>
                <Tab>List Product</Tab>
                <Tab>List Flyer</Tab>
                <Tab>List Iframe</Tab>
                <Tab>Reporting</Tab>
              </TabList>

              <TabPanels>
                {/* Tab How To Publish */}
                <TabPanel>
                  <IeemPublishTab memberCode={getMemberCode()} />
                </TabPanel>

                {/* Tab List Product */}
                <TabPanel>
                        <VStack align="stretch" spacing={4} mb={6}>
                          <HStack spacing={3} flexWrap="wrap">
                            <Button
                              leftIcon={<FiRefreshCw />}
                              onClick={fetchHotels}
                              isLoading={loading}
                              variant="outline"
                              colorScheme="brand"
                              size={{ base: 'sm', lg: 'md' }}
                            >
                              Refresh
                            </Button>
                            <Button
                              leftIcon={<FiPlus />}
                              onClick={handleCreate}
                              colorScheme="brand"
                              size={{ base: 'sm', lg: 'md' }}
                            >
                              <Text display={{ base: 'none', sm: 'block' }}>Tambah Hotel</Text>
                              <Text display={{ base: 'block', sm: 'none' }}>Tambah</Text>
                            </Button>
                          </HStack>
                        </VStack>

                        <DataTable
                          columns={columns}
                          data={filteredHotels}
                          progressPending={loading}
                          progressComponent={<Text py={8}>Loading...</Text>}
                          noDataComponent={
                            <Text py={8} color="gray.500" textAlign="center">
                              Tidak ada data hotel
                            </Text>
                          }
                          subHeader
                          subHeaderComponent={
                            <Input
                              placeholder="Cari hotel (nama, lokasi, bintang, ID, Hotel Code)..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              mb={4}
                              maxW="400px"
                            />
                          }
                          pagination
                          paginationPerPage={10}
                          paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                          paginationComponentOptions={paginationComponentOptions}
                          highlightOnHover
                          pointerOnHover
                          customStyles={customStyles}
                          responsive
                          dense
                          striped
                        />
                      </TabPanel>

                {/* Tab List Flyer */}
                <TabPanel>
                  <IeemFlyerTab memberCode={getMemberCode()} />
                </TabPanel>

                {/* Tab List Iframe */}
                <TabPanel>
                  <IeemIframeTab memberCode={getMemberCode()} />
                </TabPanel>

                {/* Tab Reporting */}
                <TabPanel>
                  <VStack align="stretch" spacing={4}>
                    <Text color="gray.600">
                      Halaman Reporting untuk Injourney Hospitality.
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                      Fitur akan segera tersedia.
                    </Text>
                  </VStack>
                </TabPanel>

              </TabPanels>
            </Tabs>
          ) : isBundlingProductRoute && hasRoleBp ? (
            <Tabs isLazy lazyBehavior="keepMounted">
              <TabList>
                <Tab>List Product</Tab>
                <Tab>Hotel Master</Tab>
                <Tab>Availability Requests</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <BundlingProductTab memberId={getMemberCode()} />
                </TabPanel>

                <TabPanel>
                  <HinHotelMaster
                    embedded
                    initialActive="master"
                    sections={[
                      ['master', 'Master Hotel'],
                      ['import', 'Import Hotel'],
                      ['history', 'Riwayat Import'],
                    ]}
                  />
                </TabPanel>

                <TabPanel>
                  <HinHotelMaster
                    embedded
                    initialActive="availability"
                    sections={[['availability', 'Permintaan Ketersediaan']]}
                    showSectionTabs={false}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            // Default - redirect ke IEEM
            <VStack align="stretch" spacing={4}>
              <Text color="gray.600">
                Memuat...
              </Text>
            </VStack>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingHotel ? 'Edit Hotel' : 'Tambah Hotel Baru'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Hotel Code</FormLabel>
                <Input
                  name="HOTEL_CODE"
                  value={formData.HOTEL_CODE}
                  onChange={handleInputChange}
                  placeholder="Kode hotel (mis. 4 huruf untuk integrasi FTP)"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Nama Hotel</FormLabel>
                <Input
                  name="HOTEL_NAME"
                  value={formData.HOTEL_NAME}
                  onChange={handleInputChange}
                  placeholder="Masukkan nama hotel"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Bintang</FormLabel>
                <Select
                  name="HOTEL_STAR"
                  value={formData.HOTEL_STAR}
                  onChange={handleInputChange}
                  placeholder="Pilih bintang"
                >
                  <option value="">Pilih bintang</option>
                  <option value="1">Bintang 1</option>
                  <option value="2">Bintang 2</option>
                  <option value="3">Bintang 3</option>
                  <option value="4">Bintang 4</option>
                  <option value="5">Bintang 5</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Lokasi</FormLabel>
                <Input
                  name="HOTEL_LOCATION"
                  value={formData.HOTEL_LOCATION}
                  onChange={handleInputChange}
                  placeholder="Masukkan lokasi hotel"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Deskripsi</FormLabel>
                <Textarea
                  name="HOTEL_DESCRIPTION"
                  value={formData.HOTEL_DESCRIPTION}
                  onChange={handleInputChange}
                  placeholder="Masukkan deskripsi hotel"
                  rows={4}
                />
              </FormControl>

              {/* Room Option - Hidden, now using Room Types instead */}
              {/* <FormControl>
                <FormLabel>Room Option</FormLabel>
                <CheckboxGroup
                  value={formData.HOTEL_ROOMOPTION || []}
                  onChange={handleRoomOptionChange}
                  colorScheme="brand"
                >
                  <VStack align="start" spacing={2} maxH="300px" overflowY="auto" p={2} border="1px solid" borderColor="gray.200" borderRadius="md">
                    {roomOptions.map((option) => (
                      <Checkbox key={option} value={option}>
                        {option}
                      </Checkbox>
                    ))}
                  </VStack>
                </CheckboxGroup>
              </FormControl> */}

              {/* Price - Hidden, now using Room Types instead */}
              {/* <FormControl>
                <FormLabel>Price</FormLabel>
                <Input
                  name="HOTEL_DISCOUNT"
                  value={formData.HOTEL_DISCOUNT}
                  onChange={handleInputChange}
                  placeholder="Masukkan price"
                  type="number"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Fixed Price</FormLabel>
                <Input
                  name="HOTEL_FIXEDPRICE"
                  value={formData.HOTEL_FIXEDPRICE}
                  onChange={handleInputChange}
                  placeholder="Masukkan fixed price"
                  type="text"
                />
              </FormControl> */}

              <FormControl>
                <FormLabel>Publish Rate</FormLabel>
                <Select
                  name="HOTEL_PUBLISHRATE"
                  value={formData.HOTEL_PUBLISHRATE}
                  onChange={handleInputChange}
                >
                  <option value="0">Not Active</option>
                  <option value="1">Active</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Contact Person</FormLabel>
                <Input
                  name="HOTEL_CONTACTPERSON"
                  value={formData.HOTEL_CONTACTPERSON}
                  onChange={handleInputChange}
                  placeholder="Masukkan contact person"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input
                  name="HOTEL_PHONE"
                  value={formData.HOTEL_PHONE}
                  onChange={handleInputChange}
                  placeholder="Masukkan nomor telepon"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  name="HOTEL_EMAIL"
                  value={formData.HOTEL_EMAIL}
                  onChange={handleInputChange}
                  placeholder="Email reservasi hotel (penerima konfirmasi pemesanan)"
                  type="email"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Nomor Rekening</FormLabel>
                <Input
                  name="HOTEL_BANKACCOUNT"
                  value={formData.HOTEL_BANKACCOUNT}
                  onChange={handleInputChange}
                  placeholder="Nomor rekening hotel untuk settlement"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Nama Bank</FormLabel>
                <Input
                  name="HOTEL_BANKNAME"
                  value={formData.HOTEL_BANKNAME}
                  onChange={handleInputChange}
                  placeholder="Contoh: BCA, Mandiri, BNI"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Term & Condition</FormLabel>
                <Textarea
                  name="HOTEL_TERMCONDITION"
                  value={formData.HOTEL_TERMCONDITION}
                  onChange={handleInputChange}
                  placeholder="Masukkan term & condition"
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Message</FormLabel>
                <Textarea
                  name="HOTEL_MSG"
                  value={formData.HOTEL_MSG}
                  onChange={handleInputChange}
                  placeholder="Masukkan message"
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Booking URL</FormLabel>
                <Input
                  name="HOTEL_BOOKINGURL"
                  value={formData.HOTEL_BOOKINGURL}
                  onChange={handleInputChange}
                  placeholder="Masukkan booking URL"
                  type="url"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  name="HOTEL_STATUS"
                  value={formData.HOTEL_STATUS}
                  onChange={handleInputChange}
                >
                  <option value="1">Active (Show)</option>
                  <option value="0">Not Active (Hide)</option>
                </Select>
              </FormControl>

            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Batal
            </Button>
            <Button colorScheme="brand" onClick={handleSubmit}>
              {editingHotel ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Detail Hotel
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingHotel && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Nama Hotel</Text>
                  <Text fontSize="md" fontWeight="600" color="gray.800">{viewingHotel.HOTEL_NAME || '-'}</Text>
                </Box>

                <HStack spacing={4}>
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Bintang</Text>
                    <Badge colorScheme="yellow">
                      {viewingHotel.HOTEL_STAR} ⭐
                    </Badge>
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Lokasi</Text>
                    <Text fontSize="md" color="gray.700">{viewingHotel.HOTEL_LOCATION || '-'}</Text>
                  </Box>
                </HStack>

                {viewingHotel.HOTEL_DESCRIPTION && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Deskripsi</Text>
                    <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{viewingHotel.HOTEL_DESCRIPTION}</Text>
                  </Box>
                )}

                {viewingHotel.HOTEL_ROOMOPTION && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Room Option</Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {(() => {
                        const roomOptions = typeof viewingHotel.HOTEL_ROOMOPTION === 'string' 
                          ? viewingHotel.HOTEL_ROOMOPTION.split(/[;,]/).map(r => r.trim()).filter(r => r)
                          : (Array.isArray(viewingHotel.HOTEL_ROOMOPTION) ? viewingHotel.HOTEL_ROOMOPTION : []);
                        return roomOptions.map((option, index) => (
                          <Badge key={index} colorScheme="blue" fontSize="sm">
                            {option}
                          </Badge>
                        ));
                      })()}
                    </HStack>
                  </Box>
                )}

                {/* Price - Hidden, now using Room Types instead */}
                {/* <HStack spacing={4}>
                  {viewingHotel.HOTEL_DISCOUNT && (
                    <Box flex={1}>
                      <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Price</Text>
                      <Text fontSize="md" color="gray.700" fontWeight="600">
                        {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(Number(viewingHotel.HOTEL_DISCOUNT))}
                      </Text>
                    </Box>
                  )}
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Publish Rate</Text>
                    <Badge colorScheme={viewingHotel.HOTEL_PUBLISHRATE === 1 || viewingHotel.HOTEL_PUBLISHRATE === '1' ? 'green' : 'red'}>
                      {viewingHotel.HOTEL_PUBLISHRATE === 1 || viewingHotel.HOTEL_PUBLISHRATE === '1' ? 'Active' : 'Not Active'}
                    </Badge>
                  </Box>
                </HStack> */}

                <Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Publish Rate</Text>
                  <Badge colorScheme={viewingHotel.HOTEL_PUBLISHRATE === 1 || viewingHotel.HOTEL_PUBLISHRATE === '1' ? 'green' : 'red'}>
                    {viewingHotel.HOTEL_PUBLISHRATE === 1 || viewingHotel.HOTEL_PUBLISHRATE === '1' ? 'Active' : 'Not Active'}
                  </Badge>
                </Box>

                {(viewingHotel.HOTEL_CONTACTPERSON || viewingHotel.HOTEL_PHONE || viewingHotel.HOTEL_EMAIL || viewingHotel.HOTEL_BANKACCOUNT) && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>Kontak & Rekening</Text>
                    <VStack align="stretch" spacing={2}>
                      {viewingHotel.HOTEL_CONTACTPERSON && (
                        <HStack>
                          <Text fontSize="xs" color="gray.500" minW="120px">Contact Person:</Text>
                          <Text fontSize="sm" color="gray.700">{viewingHotel.HOTEL_CONTACTPERSON}</Text>
                        </HStack>
                      )}
                      {viewingHotel.HOTEL_PHONE && (
                        <HStack>
                          <Text fontSize="xs" color="gray.500" minW="120px">Phone:</Text>
                          <Text fontSize="sm" color="gray.700">{viewingHotel.HOTEL_PHONE}</Text>
                        </HStack>
                      )}
                      {viewingHotel.HOTEL_EMAIL && (
                        <HStack>
                          <Text fontSize="xs" color="gray.500" minW="120px">Email:</Text>
                          <Text fontSize="sm" color="gray.700">{viewingHotel.HOTEL_EMAIL}</Text>
                        </HStack>
                      )}
                      {(viewingHotel.HOTEL_BANKACCOUNT || viewingHotel.HOTEL_BANKNAME) && (
                        <HStack>
                          <Text fontSize="xs" color="gray.500" minW="120px">Rekening:</Text>
                          <Text fontSize="sm" color="gray.700">
                            {[viewingHotel.HOTEL_BANKNAME, viewingHotel.HOTEL_BANKACCOUNT].filter(Boolean).join(' · ')}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </Box>
                )}

                {viewingHotel.HOTEL_BOOKINGURL && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Booking URL</Text>
                    <Text fontSize="sm" color="blue.600" as="a" href={viewingHotel.HOTEL_BOOKINGURL} target="_blank" rel="noopener noreferrer" wordBreak="break-all">
                      {viewingHotel.HOTEL_BOOKINGURL}
                    </Text>
                  </Box>
                )}

                {viewingHotel.HOTEL_TERMCONDITION && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Term & Condition</Text>
                    <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{viewingHotel.HOTEL_TERMCONDITION}</Text>
                  </Box>
                )}

                {viewingHotel.HOTEL_MSG && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Message</Text>
                    <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{viewingHotel.HOTEL_MSG}</Text>
                  </Box>
                )}

                <Divider />

                {/* Room Types Section */}
                <Box>
                  <HStack justify="space-between" mb={3}>
                    <Text fontSize="sm" fontWeight="600" color="gray.700">Room Types</Text>
                    <Button 
                      size="sm" 
                      colorScheme="brand" 
                      leftIcon={<FiPlus />} 
                      onClick={() => {
                        if (viewingHotel) {
                          handleCreateRoomType(viewingHotel);
                        } else {
                          handleCreateRoomType();
                        }
                      }}
                    >
                      Tambah Room Type
                    </Button>
                  </HStack>
                  {loadingRoomTypes ? (
                    <VStack py={4}>
                      <Spinner size="sm" />
                      <Text fontSize="xs" color="gray.500">Memuat room types...</Text>
                    </VStack>
                  ) : roomTypes.length > 0 ? (
                    <VStack align="stretch" spacing={2}>
                      {roomTypes.map((roomType) => (
                        <Box key={roomType.ROOMTYPE_ID} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
                          <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="600" color="gray.800">
                              {roomType.ROOMTYPE_NAME}
                              {roomType.ROOMTYPE_CODE && (
                                <Badge ml={2} colorScheme="blue" fontSize="xs">
                                  {roomType.ROOMTYPE_CODE}
                                </Badge>
                              )}
                            </Text>
                            <HStack>
                              <IconButton
                                icon={<FiEdit />}
                                size="xs"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => handleEditRoomType(roomType)}
                                aria-label="Edit"
                              />
                              <IconButton
                                icon={<FiTrash2 />}
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleDeleteRoomTypeClick(roomType)}
                                aria-label="Delete"
                              />
                            </HStack>
                          </HStack>
                          <VStack align="stretch" spacing={1} fontSize="xs" color="gray.600">
                            {(roomType.ROOMTYPE_BASEOCCUPANCY || roomType.ROOMTYPE_MAXOCCUPANCY) && (
                              <HStack>
                                <Text minW="120px">Occupancy:</Text>
                                <Text>
                                  {roomType.ROOMTYPE_BASEOCCUPANCY || '-'} - {roomType.ROOMTYPE_MAXOCCUPANCY || '-'} orang
                                </Text>
                              </HStack>
                            )}
                            {(roomType.ROOMTYPE_DISCOUNTPRICE || roomType.ROOMTYPE_FIXEDPRICE) && (
                              <HStack>
                                <Text minW="120px">Harga:</Text>
                                <HStack spacing={2}>
                                {roomType.ROOMTYPE_FIXEDPRICE && (
                                  <Text textDecoration={roomType.ROOMTYPE_DISCOUNTPRICE ? 'line-through' : 'none'} color="gray.500">
                                    {formatRupiah(roomType.ROOMTYPE_FIXEDPRICE)}
                                  </Text>
                                )}
                                {roomType.ROOMTYPE_DISCOUNTPRICE && (
                                  <Text fontWeight="600" color="blue.600">
                                    {formatRupiah(roomType.ROOMTYPE_DISCOUNTPRICE)}
                                  </Text>
                                )}
                                </HStack>
                              </HStack>
                            )}
                            {roomType.ROOMTYPE_DESCRIPTION && (
                              <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap">
                                {roomType.ROOMTYPE_DESCRIPTION}
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                      Belum ada room types. Klik "Tambah Room Type" untuk menambahkan.
                    </Text>
                  )}
                </Box>

                <Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Status</Text>
                  <Badge colorScheme={viewingHotel.HOTEL_STATUS === '1' || viewingHotel.HOTEL_STATUS === 1 ? 'green' : 'red'}>
                    {viewingHotel.HOTEL_STATUS === '1' || viewingHotel.HOTEL_STATUS === 1 ? 'Active (Show)' : 'Not Active (Hide)'}
                  </Badge>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>Tutup</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Room Types Manage Modal - Single modal for list and form */}
      <Modal 
        isOpen={isRoomTypeFormOpen} 
        onClose={onRoomTypeFormClose} 
        size="xl" 
        scrollBehavior="inside" 
        isCentered
        blockScrollOnMount={true}
        closeOnOverlayClick={true}
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" zIndex={1400} />
        <ModalContent zIndex={1500} maxH="90vh">
          <ModalHeader>
            Kelola Room Types - {editingHotel?.HOTEL_NAME || viewingHotel?.HOTEL_NAME || 'Hotel'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody 
            overflowY="auto" 
            maxH="calc(90vh - 180px)"
            onWheel={(e) => {
              e.stopPropagation();
            }}
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
            }}
          >
            <VStack spacing={6} align="stretch">
              {/* Room Types List Section */}
              <Box>
                <HStack justify="space-between" mb={3}>
                  <Text fontSize="md" fontWeight="600" color="gray.700">Daftar Room Types</Text>
                  <Button size="sm" colorScheme="brand" leftIcon={<FiPlus />} onClick={() => {
                    setEditingRoomType(null);
                    setRoomTypeFormData({
                      ROOMTYPE_CODE: '',
                      ROOMTYPE_NAME: '',
                      ROOMTYPE_BASEOCCUPANCY: '',
                      ROOMTYPE_MAXOCCUPANCY: '',
                      ROOMTYPE_DESCRIPTION: '',
                      ROOMTYPE_DISCOUNTPRICE: '',
                      ROOMTYPE_FIXEDPRICE: '',
                    });
                  }}>
                    Tambah Baru
                  </Button>
                </HStack>
                {loadingRoomTypes ? (
                  <VStack py={4}>
                    <Spinner size="sm" />
                    <Text fontSize="xs" color="gray.500">Memuat room types...</Text>
                  </VStack>
                ) : roomTypes.length > 0 ? (
                  <VStack align="stretch" spacing={2} maxH="250px" overflowY="auto" css={{
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                    },
                  }}>
                    {roomTypes.map((roomType) => (
                      <Box key={roomType.ROOMTYPE_ID} p={3} border="1px solid" borderColor={editingRoomType?.ROOMTYPE_ID === roomType.ROOMTYPE_ID ? 'brand.500' : 'gray.200'} borderRadius="md" bg={editingRoomType?.ROOMTYPE_ID === roomType.ROOMTYPE_ID ? 'brand.50' : 'white'}>
                        <HStack justify="space-between" mb={2}>
                          <Text fontSize="sm" fontWeight="600" color="gray.800">
                            {roomType.ROOMTYPE_NAME}
                            {roomType.ROOMTYPE_CODE && (
                              <Badge ml={2} colorScheme="blue" fontSize="xs">
                                {roomType.ROOMTYPE_CODE}
                              </Badge>
                            )}
                          </Text>
                          <HStack>
                            <IconButton
                              icon={<FiEdit />}
                              size="xs"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleEditRoomType(roomType)}
                              aria-label="Edit"
                            />
                            <IconButton
                              icon={<FiTrash2 />}
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDeleteRoomTypeClick(roomType)}
                              aria-label="Delete"
                            />
                          </HStack>
                        </HStack>
                        <VStack align="stretch" spacing={1} fontSize="xs" color="gray.600">
                          {(roomType.ROOMTYPE_BASEOCCUPANCY || roomType.ROOMTYPE_MAXOCCUPANCY) && (
                            <HStack>
                              <Text minW="120px">Occupancy:</Text>
                              <Text>
                                {roomType.ROOMTYPE_BASEOCCUPANCY || '-'} - {roomType.ROOMTYPE_MAXOCCUPANCY || '-'} orang
                              </Text>
                            </HStack>
                          )}
                          {(roomType.ROOMTYPE_DISCOUNTPRICE || roomType.ROOMTYPE_FIXEDPRICE) && (
                            <HStack>
                              <Text minW="120px">Harga:</Text>
                              <HStack spacing={2}>
                                {roomType.ROOMTYPE_FIXEDPRICE && (
                                  <Text textDecoration={roomType.ROOMTYPE_DISCOUNTPRICE ? 'line-through' : 'none'} color="gray.500">
                                    {formatRupiah(roomType.ROOMTYPE_FIXEDPRICE)}
                                  </Text>
                                )}
                                {roomType.ROOMTYPE_DISCOUNTPRICE && (
                                  <Text fontWeight="600" color="blue.600">
                                    {formatRupiah(roomType.ROOMTYPE_DISCOUNTPRICE)}
                                  </Text>
                                )}
                              </HStack>
                            </HStack>
                          )}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                    Belum ada room types. Gunakan form di bawah untuk menambahkan.
                  </Text>
                )}
              </Box>

              <Divider />

              {/* Room Type Form Section */}
              <Box>
                <Text fontSize="md" fontWeight="600" color="gray.700" mb={4}>
                  {editingRoomType ? 'Edit Room Type' : 'Tambah Room Type Baru'}
                </Text>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Room Type Code</FormLabel>
                    <Input
                      name="ROOMTYPE_CODE"
                      value={roomTypeFormData.ROOMTYPE_CODE}
                      onChange={(e) => setRoomTypeFormData({ ...roomTypeFormData, ROOMTYPE_CODE: e.target.value })}
                      placeholder="Masukkan kode room type (opsional)"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Room Type Name</FormLabel>
                    <Input
                      name="ROOMTYPE_NAME"
                      value={roomTypeFormData.ROOMTYPE_NAME}
                      onChange={(e) => setRoomTypeFormData({ ...roomTypeFormData, ROOMTYPE_NAME: e.target.value })}
                      placeholder="Masukkan nama room type"
                    />
                  </FormControl>

                  <HStack spacing={4} w="100%">
                    <FormControl>
                      <FormLabel>Base Occupancy</FormLabel>
                      <Input
                        name="ROOMTYPE_BASEOCCUPANCY"
                        value={roomTypeFormData.ROOMTYPE_BASEOCCUPANCY}
                        onChange={(e) => setRoomTypeFormData({ ...roomTypeFormData, ROOMTYPE_BASEOCCUPANCY: e.target.value })}
                        placeholder="Min"
                        type="number"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Max Occupancy</FormLabel>
                      <Input
                        name="ROOMTYPE_MAXOCCUPANCY"
                        value={roomTypeFormData.ROOMTYPE_MAXOCCUPANCY}
                        onChange={(e) => setRoomTypeFormData({ ...roomTypeFormData, ROOMTYPE_MAXOCCUPANCY: e.target.value })}
                        placeholder="Max"
                        type="number"
                      />
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      name="ROOMTYPE_DESCRIPTION"
                      value={roomTypeFormData.ROOMTYPE_DESCRIPTION}
                      onChange={(e) => setRoomTypeFormData({ ...roomTypeFormData, ROOMTYPE_DESCRIPTION: e.target.value })}
                      placeholder="Masukkan deskripsi room type"
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Discount Price</FormLabel>
                    <Input
                      name="ROOMTYPE_DISCOUNTPRICE"
                      value={roomTypeFormData.ROOMTYPE_DISCOUNTPRICE}
                      onChange={(e) => setRoomTypeFormData({ ...roomTypeFormData, ROOMTYPE_DISCOUNTPRICE: e.target.value })}
                      placeholder="Masukkan discount price"
                      type="text"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Fixed Price</FormLabel>
                    <Input
                      name="ROOMTYPE_FIXEDPRICE"
                      value={roomTypeFormData.ROOMTYPE_FIXEDPRICE}
                      onChange={(e) => setRoomTypeFormData({ ...roomTypeFormData, ROOMTYPE_FIXEDPRICE: e.target.value })}
                      placeholder="Masukkan fixed price"
                      type="text"
                    />
                  </FormControl>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRoomTypeFormClose}>
              Tutup
            </Button>
            <Button colorScheme="brand" onClick={handleSubmitRoomType}>
              {editingRoomType ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Room Type Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteRoomTypeOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteRoomTypeClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Room Type
            </AlertDialogHeader>
            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus room type <strong>{deleteRoomType?.ROOMTYPE_NAME}</strong>?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteRoomTypeClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteRoomTypeConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Hotel
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus hotel <strong>{deleteHotel?.HOTEL_NAME}</strong>?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Manage Images Modal */}
      <Modal isOpen={isImagesOpen} onClose={onImagesClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Kelola Gambar - {managingImagesHotel?.HOTEL_NAME}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {/* Upload Section */}
              <Box>
                <FormControl>
                  <FormLabel>Upload Gambar Baru (Multiple)</FormLabel>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Format: JPG, PNG, GIF. Maksimal 5MB per file. Bisa upload beberapa file sekaligus.
                  </Text>
                </FormControl>
                {uploadingImage && (
                  <HStack mt={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.600">Mengupload gambar...</Text>
                  </HStack>
                )}
              </Box>

              <Divider />

              {/* Images List */}
              <Box>
                <Text fontSize="md" fontWeight="600" mb={3}>
                  Daftar Gambar ({hotelImages.length})
                </Text>
                {loadingImages ? (
                  <VStack py={8}>
                    <Spinner size="lg" />
                    <Text color="gray.500">Memuat gambar...</Text>
                  </VStack>
                ) : hotelImages.length === 0 ? (
                  <Text py={8} color="gray.500" textAlign="center">
                    Belum ada gambar untuk hotel ini
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {hotelImages.map((image) => (
                      <Box
                        key={image.IMAGEHOTEL_ID}
                        p={4}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="8px"
                      >
                        <HStack justify="space-between" align="start">
                          <VStack align="start" spacing={2} flex={1}>
                            <Text fontSize="sm" fontWeight="500">
                              {image.IMAGEHOTEL_NAMEFILE}
                            </Text>
                            <Image
                              src={`/images/hotels/${image.IMAGEHOTEL_NAMEFILE}`}
                              alt={image.IMAGEHOTEL_NAMEFILE || 'Hotel image'}
                              maxH="150px"
                              maxW="200px"
                              objectFit="contain"
                              fallback={
                                <Box
                                  w="200px"
                                  h="150px"
                                  bg="gray.100"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  borderRadius="4px"
                                >
                                  <Text fontSize="xs" color="gray.500">
                                    Gambar tidak ditemukan
                                  </Text>
                                </Box>
                              }
                            />
                          </VStack>
                          <IconButton
                            icon={<FiTrash2 />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDeleteImageClick(image)}
                            aria-label="Delete Image"
                          />
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onImagesClose}>Tutup</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Image Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteImageOpen}
        leastDestructiveRef={cancelImageRef}
        onClose={onDeleteImageClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Gambar
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus gambar <strong>{deleteImage?.IMAGEHOTEL_NAMEFILE}</strong>?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelImageRef} onClick={onDeleteImageClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteImageConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

    </Box>
  );
};

export default Hospitality;
